import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { Search, Trash2, X, Loader } from "lucide-react";
import { adminServices } from "../services/AdminServices";
import { toast } from "react-toastify";

// Set app element for react-modal
if (typeof window !== 'undefined' && document.getElementById('root')) {
  Modal.setAppElement('#root');
}

const UomManager = ({ isOpen, onClose, onSuccess }) => {
  const [uomName, setUomName] = useState("");
  const [uomList, setUomList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch UOM list when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUomList();
    }
  }, [isOpen]);

  const fetchUomList = async () => {
    setLoading(true);
    try {
      const response = await adminServices.getUnitOfMeasurements();
      if (response?.data) {
        const uoms = Array.isArray(response.data) ? response.data : response.data.data || [];
        setUomList(uoms);
      } else {
        setUomList([]);
      }
    } catch (error) {
      console.error("Error fetching UOM list:", error);
      toast.error("Failed to load Unit of Measurements");
      setUomList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUom = async () => {
    // Validate input
    if (!uomName.trim()) {
      toast.error("Please enter a UOM name");
      return;
    }

    if (uomName.trim().length < 2) {
      toast.error("UOM name must be at least 2 characters");
      return;
    }

    setSaving(true);
    try {
      const response = await adminServices.createUnitOfMeasurement({ name: uomName.trim() });
      
      if (response?.data?.success !== false && response?.data?.id) {
        toast.success(response?.data?.message || "Unit of Measurement added successfully");
        setUomName("");
        await fetchUomList();
        onSuccess?.();
      } else {
        toast.error(response?.data?.message || "Failed to add Unit of Measurement");
      }
    } catch (error) {
      console.error("Error adding UOM:", error);
      toast.error(error?.response?.data?.message || "Failed to add Unit of Measurement");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUom = async (uomId) => {
    if (!window.confirm("Are you sure you want to delete this Unit of Measurement?")) {
      return;
    }

    setSaving(true);
    try {
      const response = await adminServices.deleteUnitOfMeasurement(uomId);
      
      if (response?.data?.success !== false) {
        toast.success(response?.data?.message || "Unit of Measurement deleted successfully");
        await fetchUomList();
        onSuccess?.();
      } else {
        toast.error(response?.data?.message || "Failed to delete Unit of Measurement");
      }
    } catch (error) {
      console.error("Error deleting UOM:", error);
      toast.error(error?.response?.data?.message || "Failed to delete Unit of Measurement");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleAddUom();
    }
  };

  const filteredUoms = uomList.filter(uom =>
    (uom.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="uom-manager-modal"
      overlayClassName="uom-manager-modal-overlay"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={true}
    >
      <div className="uom-manager-modal-content">
        {/* Header */}
        <div className="uom-manager-modal-header">
          <h2 className="uom-manager-modal-title">Unit of Measure Management</h2>
          <button 
            className="uom-manager-modal-close" 
            onClick={onClose}
            disabled={saving}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="uom-manager-modal-body">
          {/* Add UOM Section */}
          <div className="uom-manager-add-section">
            <label className="uom-manager-input-label">Unit of Measure Name</label>
            <div className="uom-manager-input-group">
              <input
                type="text"
                className="uom-manager-input"
                placeholder="e.g., Pieces, Kilograms, Meters"
                value={uomName}
                onChange={(e) => setUomName(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={saving}
                maxLength={100}
              />
              <button
                className="uom-manager-add-btn"
                onClick={handleAddUom}
                disabled={saving || !uomName.trim()}
              >
                {saving ? <Loader size={18} className="spinner" /> : "+"}
                Add UOM
              </button>
            </div>
          </div>

          {/* Search Section */}
          <div className="uom-manager-search-container">
            <Search className="uom-manager-search-icon" size={18} />
            <input
              type="text"
              className="uom-manager-search-input"
              placeholder="Search UOM"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* UOM List Section */}
          <div className="uom-manager-list-section">
            {loading ? (
              <div className="uom-manager-loading">
                <Loader size={24} className="spinner" />
                <p>Loading Unit of Measurements...</p>
              </div>
            ) : filteredUoms.length === 0 ? (
              <div className="uom-manager-empty">
                <p>
                  {uomList.length === 0 
                    ? "No Unit of Measurements found. Add one to get started."
                    : "No results found for your search."}
                </p>
              </div>
            ) : (
              <div className="uom-manager-table">
                <div className="uom-manager-table-header">
                  <div className="uom-manager-table-cell id-cell">ID</div>
                  <div className="uom-manager-table-cell name-cell">Name</div>
                  <div className="uom-manager-table-cell action-cell">Action</div>
                </div>
                <div className="uom-manager-table-body">
                  {filteredUoms.map((uom, index) => (
                    <div key={uom.id || index} className="uom-manager-table-row">
                      <div className="uom-manager-table-cell id-cell">
                        {uom.id}
                      </div>
                      <div className="uom-manager-table-cell name-cell">
                        {uom.name}
                      </div>
                      <div className="uom-manager-table-cell action-cell">
                        <button
                          className="uom-manager-delete-btn"
                          onClick={() => handleDeleteUom(uom.id)}
                          disabled={saving}
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .uom-manager-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .uom-manager-modal {
          position: relative;
          background: white;
          border-radius: 8px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          max-width: 600px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          outline: none;
        }

        .uom-manager-modal-content {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .uom-manager-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
          flex-shrink: 0;
        }

        .uom-manager-modal-title {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
        }

        .uom-manager-modal-close {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          color: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .uom-manager-modal-close:hover:not(:disabled) {
          background-color: #f3f4f6;
          color: #1f2937;
        }

        .uom-manager-modal-close:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .uom-manager-modal-body {
          padding: 24px;
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .uom-manager-add-section {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .uom-manager-input-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 4px;
        }

        .uom-manager-input-group {
          display: flex;
          gap: 8px;
        }

        .uom-manager-input {
          flex: 1;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          font-family: inherit;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .uom-manager-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .uom-manager-input:disabled {
          background-color: #f3f4f6;
          cursor: not-allowed;
          opacity: 0.7;
        }

        .uom-manager-add-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background-color: #002347;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .uom-manager-add-btn:hover:not(:disabled) {
          background-color: #001a34;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .uom-manager-add-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .uom-manager-search-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .uom-manager-search-icon {
          position: absolute;
          left: 12px;
          color: #9ca3af;
          pointer-events: none;
        }

        .uom-manager-search-input {
          width: 100%;
          padding: 10px 12px 10px 38px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          font-family: inherit;
          transition: border-color 0.2s;
        }

        .uom-manager-search-input:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .uom-manager-search-input:disabled {
          background-color: #f3f4f6;
          cursor: not-allowed;
        }

        .uom-manager-list-section {
          flex: 1;
          overflow-y: auto;
          min-height: 200px;
        }

        .uom-manager-loading,
        .uom-manager-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 40px 24px;
          color: #6b7280;
          text-align: center;
        }

        .uom-manager-table {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          overflow: hidden;
        }

        .uom-manager-table-header {
          display: grid;
          grid-template-columns: 80px 1fr 80px;
          background-color: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          font-weight: 600;
          font-size: 0.875rem;
          color: #374151;
        }

        .uom-manager-table-cell {
          padding: 12px;
          text-align: left;
        }

        .uom-manager-table-cell.id-cell {
          text-align: center;
        }

        .uom-manager-table-cell.action-cell {
          text-align: center;
        }

        .uom-manager-table-body {
          display: flex;
          flex-direction: column;
        }

        .uom-manager-table-row {
          display: grid;
          grid-template-columns: 80px 1fr 80px;
          border-bottom: 1px solid #e5e7eb;
          transition: background-color 0.2s;
        }

        .uom-manager-table-row:hover {
          background-color: #f9fafb;
        }

        .uom-manager-table-row:last-child {
          border-bottom: none;
        }

        .uom-manager-table-row .uom-manager-table-cell {
          padding: 12px;
          font-size: 0.875rem;
          color: #1f2937;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .uom-manager-table-row .uom-manager-table-cell.id-cell {
          text-align: center;
        }

        .uom-manager-table-row .uom-manager-table-cell.action-cell {
          text-align: center;
        }

        .uom-manager-delete-btn {
          background: none;
          border: none;
          padding: 6px;
          cursor: pointer;
          color: #ef4444;
          border-radius: 4px;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .uom-manager-delete-btn:hover:not(:disabled) {
          background-color: #fee2e2;
        }

        .uom-manager-delete-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </Modal>
  );
};

export default UomManager;
